// Eigener Locate-Control statt MapLibre's GeolocateControl.
//
// Hintergrund Bug #3:
//   Der eingebaute GeolocateControl verliert auf iOS Safari PWA nach dem
//   ersten Zentrieren schnell wieder das Tracking. Ursache: der Control
//   reagiert auf JEDES movestart-Event, dessen originalEvent != null ist,
//   mit Transition ACTIVE_LOCK → BACKGROUND. Auf iOS feuert nach einem
//   Button-Tap aber manchmal ein Phantom-touchstart durch den Canvas (iOS
//   leaked Button-Touches in darunterliegende Elements), welches wie eine
//   User-Pan-Geste aussieht → Tracking kippt sofort.
//
// Diese Implementation:
//   - State-Machine: off → waiting → active ↔ paused → off
//   - User-Geste zum Pausieren erkennen wir EXPLIZIT über 'dragstart',
//     'wheel', 'pitchstart', 'rotatestart' mit originalEvent — nicht über
//     generisches movestart. Unsere eigenen easeTo/flyTo mit essential:true
//     triggern diese Events ohne originalEvent, sind also transparent.
//   - Wir haben keine touchstart-listener auf der Map — das ist die eine
//     Sache die den Bug verursacht, und Pan wird bereits von dragstart
//     abgedeckt, was erst bei tatsächlicher Bewegung feuert.
//   - Eigener Marker (orange Kompass-Pfeil, Wave 14k — vorher blauer Punkt +
//     pulsierende Aura, aber die Aura war eine rAF-Dauerschleife @ 60fps und
//     der Pfeil gibt mehr Info als der Kreis) statt MapLibre's defaultem
//     user-dot, damit wir nicht auf Control-Internals angewiesen sind.

import maplibregl from 'maplibre-gl';
import type { ControlPosition, IControl, Map as MLMap } from 'maplibre-gl';
import { positionBus } from '../positionBus';
import { deviceHeadingBus } from './deviceHeadingBus';

type State = 'off' | 'waiting' | 'active' | 'paused' | 'error';

/**
 * Ride vs Explore: bestimmt wie aggressiv der Control GPS-Fixes anfragt.
 * Wave 14bo (Energiespar-Tipp #4): im Explore-Mode haben wir i. d. R. keinen
 * aktiven Follow-Flug — der User schaut sich die Map an, scrollt, wählt
 * einen Trail aus. Ein 1-Sekunden-maximumAge hält das GPS-Radio unnötig hot.
 * Ride-Mode bleibt aggressiv, weil Follow-Kamera auf jeden Fix reagieren muss.
 */
export type LocateTrackingMode = 'ride' | 'explore';

export interface LocateControlOptions {
  /**
   * Fester Ziel-Zoom beim Fly-to-User. Wenn undefined: map.getMaxZoom() —
   * also maximale Detail-Stufe der Karte. Das ist das vom User gewünschte
   * Verhalten: "beim Zentrieren direkt auf die höchste Zoomstufe".
   */
  lockZoom?: number;
  /** Dauer des initialen flyTo in ms. Default 800. */
  flyDuration?: number;
  /** Dauer des follow-easeTo bei jedem Positions-Update in ms. Default 500. */
  followDuration?: number;
  /**
   * Wenn true: startet GPS-Tracking automatisch beim Mount (nach map.load).
   * Der User muss nicht tappen, die Karte zentriert sich sofort.
   * iOS fragt beim ersten Start nach Geolocation-Permission — das ist der
   * native Prompt-Flow, also ok ohne User-Gesture.
   */
  autoStart?: boolean;
  /**
   * Tracking-Mode beim Control-Start. Default 'explore' — MapPage schaltet
   * per setMode('ride') hoch sobald der User einen Trail aktiviert.
   */
  trackingMode?: LocateTrackingMode;
  /**
   * Stage 6m: One-shot-Modus. Wenn true, wird beim ersten Position-Fix
   * EINMAL zur User-Position geflogen, dann sofort in 'paused' geswitcht
   * statt in 'active' — kein Auto-Follow, der User kann frei pannen ohne
   * dass die Karte ihn zurückzieht. Re-Tap des Buttons re-zentriert
   * (bleibt aber 'paused', kein Follow).
   *
   * Gedacht für den NAVI-Plan-Modus, wo der User die Karte hin- und
   * her bewegt um Trails auszuwählen. User-Bug-Report 14ce-fix2: "der
   * button bleibt dann aus, so dass ich die karte frei verschieben kann.
   * sonst kommt sie immer wieder in die mitte zrucük".
   */
  oneShot?: boolean;
}

/**
 * watchPosition-Options je Tracking-Mode. Wir wechseln bewusst NUR maximumAge
 * und nicht enableHighAccuracy — wir wollen weiter GPS-Fixes (und kein WiFi-
 * Triangulations-Fallback auf 100m Genauigkeit).
 *
 * Wave 14bo-fix: im Explore-Mode erwartet der User eigentlich auch
 * responsive GPS (weil er in Explore Fahrrad fährt, nicht nur sitzt und
 * schaut). 5s fühlt sich beim Radfahren laggy an ("ist das noch mein
 * Punkt?"). Kompromiss: Ride 1s, Explore 2s — immer noch ein kleiner
 * Atem-Raum für die GPS-HW, aber der Positions-Dot bleibt visuell snappy.
 * Tatsächliche Akku-Ersparnis von maximumAge allein ist eh marginal, weil
 * die GPS-Radio-Hardware bei enableHighAccuracy:true ohnehin im Dauer-
 * Modus läuft — maximumAge beeinflusst primär das Caching zwischen
 * verschiedenen Watchern/Requests.
 */
function watchOptionsFor(mode: LocateTrackingMode): PositionOptions {
  switch (mode) {
    case 'ride':
      return { enableHighAccuracy: true, timeout: 15_000, maximumAge: 1_000 };
    case 'explore':
      return { enableHighAccuracy: true, timeout: 15_000, maximumAge: 2_000 };
  }
}

export class LocateControl implements IControl {
  private _map?: MLMap;
  private _container!: HTMLDivElement;
  private _button!: HTMLButtonElement;
  private _marker: maplibregl.Marker | null = null;
  private _watchId: number | null = null;
  private _state: State = 'off';
  private _lastCoords: GeolocationCoordinates | null = null;
  private _userMoveHandler: ((e: unknown) => void) | null = null;
  /**
   * Letzte bekannte Blickrichtung des Geräts (Grad von Nord im Uhrzeigersinn,
   * 0..360). Wird von ZWEI Quellen gefüttert:
   *   1) deviceHeadingBus (DeviceOrientationEvent) — bevorzugt, weil sie die
   *      physische Blickrichtung liefert, nicht die Bewegungsrichtung. Damit
   *      dreht sich das Dreieck auch wenn der User steht und sich nur umdreht.
   *   2) GPS coords.heading als Fallback — nur wenn Device-Heading (noch) nicht
   *      verfügbar ist (z. B. Desktop ohne Sensor, oder iOS ohne Permission).
   * Default 0 (nach Norden) bis wir das erste Heading kriegen.
   *
   * Wave 14am: vorher war das nur GPS-Heading. Wenn der User stand, war
   * coords.heading null → Dreieck blieb fix auch beim Drehen → fatal im
   * 'heading up'-Map-Mode, wo die Map rotiert aber das Dreieck nicht.
   */
  private _lastHeading: number = 0;
  /** Unsubscribe-Handle für den deviceHeadingBus. null solange off. */
  private _unsubHeading: (() => void) | null = null;
  // True solange eine initiale flyTo-Animation läuft. Sperrt in dieser Zeit
  // den follow-easeTo, sonst würde der nächste Position-Fix die Animation
  // mittendrin abbrechen (easeTo ohne Zoom-Spec hält den aktuellen Zoom —
  // wir würden auf halbem Weg zum Max-Zoom hängen bleiben). Classic race:
  // mit enableHighAccuracy + maximumAge:1000 kommen die ersten Fixes im
  // Abstand von ~100-300ms, die flyTo dauert aber 800ms.
  private _flying = false;
  // lockZoom wird absichtlich NICHT auf einen Default aufgefüllt — wenn undefined
  // fallen wir zur Laufzeit auf map.getMaxZoom() zurück, und das kennen wir beim
  // Constructor-Aufruf noch nicht (Control wird vor map.load() instanziiert).
  private readonly _opts: {
    lockZoom: number | undefined;
    flyDuration: number;
    followDuration: number;
    autoStart: boolean;
    trackingMode: LocateTrackingMode;
    oneShot: boolean;
  };

  constructor(opts: LocateControlOptions = {}) {
    this._opts = {
      lockZoom: opts.lockZoom,
      flyDuration: opts.flyDuration ?? 800,
      followDuration: opts.followDuration ?? 500,
      autoStart: opts.autoStart ?? false,
      trackingMode: opts.trackingMode ?? 'explore',
      oneShot: opts.oneShot ?? false,
    };
  }

  onAdd(map: MLMap): HTMLElement {
    this._map = map;

    this._container = document.createElement('div');
    // Custom-Klasse `adt-ctrl` stellt das Turbo-Dark-Pill-Design bereit
    // (siehe styles/index.css). Wir verzichten hier bewusst auf Inline-
    // width/height/colors, damit das Styling allein von CSS kommt und
    // beim Theming nur eine Stelle angefasst werden muss.
    this._container.className = 'maplibregl-ctrl maplibregl-ctrl-group adt-ctrl';

    this._button = document.createElement('button');
    this._button.type = 'button';
    this._button.setAttribute('aria-label', 'My location');
    this._button.className = 'adt-ctrl-btn';
    // WICHTIG: stopPropagation auf pointerdown UND touchstart, damit iOS
    // den Touch nicht an den Map-Canvas durchreicht (→ Phantom-Pan-Geste).
    const stop = (e: Event) => {
      e.stopPropagation();
    };
    this._button.addEventListener('pointerdown', stop);
    this._button.addEventListener('touchstart', stop, { passive: true });
    this._button.addEventListener('click', (e) => {
      e.stopPropagation();
      this._onTap();
    });

    this._container.appendChild(this._button);
    this._renderButton();

    // AutoStart: sobald die Map geladen ist, GPS-Tracking starten. Wenn sie
    // schon geladen ist (z. B. bei Control-Re-Add nach Rebuild), sofort.
    if (this._opts.autoStart) {
      const kick = () => this._start();
      if (map.loaded()) {
        // Nicht synchron — lass onAdd erst sauber returnen und gib MapLibre
        // die Chance, den Container ins DOM einzuhängen.
        setTimeout(kick, 0);
      } else {
        map.once('load', kick);
      }
    }

    return this._container;
  }

  onRemove(): void {
    this._stop();
    this._container.parentNode?.removeChild(this._container);
    this._map = undefined;
  }

  getDefaultPosition(): ControlPosition {
    return 'top-right';
  }

  // -- Public imperative API für Ride/Explore-Mode-Switching ------------
  //
  // Der Ride-Button aus dem Trail-Detail-Sheet soll "hart" aktivieren:
  //   - Tracking starten (falls off)
  //   - aus 'paused' zurück auf 'active' (der User hatte evtl. gepannt)
  //   - auf ride-zoom fliegen (ohne den follow-easeTo des nächsten Position-
  //     Updates zu racen)
  //
  // Beim Wechsel zurück auf Explore-Mode rufen wir setLockZoom() mit dem
  // Explore-Zoom — die nächste manuelle Re-Zentrierung nutzt dann den.

  /**
   * Überschreibt den Default-Zoom für kommende flyTo-Aufrufe. `undefined`
   * fällt auf map.getMaxZoom() zurück (alter Default).
   */
  public setLockZoom(z: number | undefined): void {
    this._opts.lockZoom = z;
  }

  /**
   * Wave 14bo (Energiespar-Tipp #4): Ride/Explore-Tracking-Mode umstellen.
   * Ride-Mode fragt GPS-Fixes mit maximumAge:1000 an, Explore-Mode relaxt
   * auf maximumAge:2000 (14bo-fix: von 5000 auf 2000 reduziert, weil User
   * auch im Explore Rad fährt und der Dot-Lag spürbar war).
   *
   * Wenn gerade aktiv getrackt wird, starten wir watchPosition neu damit
   * die neuen Options wirksam werden (navigator.geolocation speichert die
   * Options pro Watch-Handle — ohne Restart greift der neue maximumAge nicht).
   * Das kurze Stream-Unterbrechung von ~1 Frame ist akzeptabel weil Mode-
   * Wechsel selten sind (einmal pro Trail-Select/Deselect).
   *
   * Idempotent: wenn der Mode schon passt, tun wir nichts.
   */
  public setMode(m: LocateTrackingMode): void {
    if (this._opts.trackingMode === m) return;
    this._opts.trackingMode = m;
    // Nur re-watchen wenn wir gerade am Tracken sind — State 'off'/'error'
    // hat keinen Watch, und 'waiting'/'paused' hat einen den wir neu aufsetzen.
    if (this._watchId != null) {
      navigator.geolocation.clearWatch(this._watchId);
      this._watchId = navigator.geolocation.watchPosition(
        (pos) => this._onPosition(pos),
        (err) => this._onError(err),
        watchOptionsFor(m),
      );
      console.info(`[locate] tracking mode → ${m}`);
    }
  }

  /**
   * Hart-Aktivierung: starte Tracking wenn nötig, zwinge aktiv-Status, und
   * fliege zum letzten bekannten Fix (oder warte auf ersten Fix wenn noch
   * keiner da ist). Optional `overrideZoom` setzt den lockZoom auf diesen
   * Wert (PERSISTENT, NICHT temporär — vorheriger Code hatte einen moveend-
   * Restore der den alten Wert zurückrollte; das war der Bug).
   *
   * Wird vom Ride-Button gerufen: Modus-Switch soll unmittelbar spürbar sein.
   *
   * Wave 14cf-fix7: Restore-Logik entfernt. Der Caller (MapPage's
   * mode-useEffect) sorgt dafür dass lockZoom immer dem aktuellen
   * Mode-Default entspricht. Der frühere Restore-Mechanismus wollte
   * nach Mode-Wechsel den persistierten Default-Wert wiederherstellen,
   * aber er snappshottete prevLock VOR dem useEffect-Run und schoss
   * dann gegen den richtigen Wert ein. User-Bug: nach Pannen + Center-
   * Tap im Ride-Mode sprang die Karte auf exploreZoom statt rideZoom.
   */
  public forceActivate(overrideZoom?: number): void {
    const map = this._map;
    if (!map) return;
    if (overrideZoom != null) this._opts.lockZoom = overrideZoom;

    // Off/Error: normales _start() übernimmt (waiting → active beim ersten Fix).
    if (this._state === 'off' || this._state === 'error') {
      this._start();
    } else if (this._state === 'paused') {
      // Pause aufheben + neu zentrieren wie beim Resume-Tap.
      this._state = 'active';
      this._renderButton();
      if (this._lastCoords) this._flyToCoords(this._lastCoords);
    } else if (this._state === 'active' && this._lastCoords) {
      // Schon aktiv: explizit wieder-fly mit (evtl. neuem) Ziel-Zoom.
      this._flyToCoords(this._lastCoords);
    }
    // Bewusst KEIN Restore mehr — siehe Methoden-Header.
    // Wir vertrauen darauf dass der Caller via setLockZoom() den korrekten
    // Wert für den aktuellen Mode setzt (siehe MapPage useEffect).
    void map;
  }

  /** Gibt den aktuellen State zurück (für UI-Indikatoren außerhalb des Controls). */
  public getState(): State {
    return this._state;
  }

  /**
   * Pausiert das Follow-Tracking ohne den GPS-Watch zu stoppen.
   *
   * User-Wunsch: "wenn oben rechts der button für aktuelle position aktiviert
   * ist, man aber einen trail anklickt, soll der button deaktiviert werden
   * und die ansicht den trail zeigen — sonst springt es wieder zu mir zurück".
   *
   * Warum nicht `_stop()`? Stop klappt den GPS-Watch zu, entfernt den Marker
   * und setzt State auf 'off'. Danach müsste der User das Tracking komplett
   * neu starten (inkl. iOS-Permission-Fliegenplaudertasche). Pause ist das
   * weichere Tool: GPS läuft weiter, Marker bleibt sichtbar und wandert mit,
   * nur die Kamera folgt nicht mehr. Der User kann mit einem Re-Tap sofort
   * zurück auf sich zentrieren.
   *
   * Idempotent: wenn nicht gerade 'active', tun wir nichts — insbesondere
   * wechseln wir aus 'waiting' NICHT in 'paused', sonst ginge der "wartet
   * auf ersten Fix"-Zustand verloren.
   */
  public pause(): void {
    if (this._state !== 'active') return;
    this._state = 'paused';
    this._renderButton();
    console.info('[locate] programmatic pause (trail selected)');
  }

  /** Primärer Tap-Handler: cycelt durch die States. */
  private _onTap(): void {
    switch (this._state) {
      case 'off':
      case 'error':
        this._start();
        break;
      case 'active':
        // Intentional aus
        this._stop();
        break;
      case 'paused':
        // Stage 6m: oneShot-Modus tappt im paused-State NICHT in 'active'
        // (sonst Auto-Follow, was wir hier explizit nicht wollen).
        // Stattdessen einfach re-zentrieren, paused bleibt.
        if (this._opts.oneShot) {
          if (this._lastCoords) this._flyToCoords(this._lastCoords);
        } else {
          // Normal: Resume → wieder zentrieren + folgen
          this._state = 'active';
          this._renderButton();
          if (this._lastCoords) this._flyToCoords(this._lastCoords);
        }
        break;
      case 'waiting':
        // Cancel Waiting
        this._stop();
        break;
    }
  }

  private _start(): void {
    if (!('geolocation' in navigator)) {
      console.warn('[locate] geolocation nicht verfügbar');
      this._state = 'error';
      this._renderButton();
      return;
    }
    this._state = 'waiting';
    this._renderButton();

    this._watchId = navigator.geolocation.watchPosition(
      (pos) => this._onPosition(pos),
      (err) => this._onError(err),
      watchOptionsFor(this._opts.trackingMode),
    );

    // Wave 14am: Dreieck an die physische Blickrichtung des Geräts koppeln.
    // Wir subscriben auf den shared deviceHeadingBus; wenn die Permission
    // noch nicht erteilt wurde, schickt der Bus keine Events, aber der
    // window-Listener hängt trotzdem schon. Sobald der User später entweder
    // die Kompass-Rose tappt (setMode('heading')) oder den Ride-Button drückt
    // (MapPage ruft auch setMode), triggert dort `requestPermissionIfNeeded()`
    // im User-Gesture-Kontext, iOS zeigt den Prompt, Events fließen hier auch
    // automatisch durch (same Bus).
    //
    // Wave 14an (revert): in einem Zwischenversuch hatte ich hier den Call
    // `void deviceHeadingBus.requestPermissionIfNeeded()` entfernt — mit der
    // Theorie, dass der fire-and-forget-Call aus dem autoStart-setTimeout
    // ohne User-Gesture den Bus-Cache auf 'denied' setzt und später den
    // Rose-Tap ausbremst. Das hat den Ride-Pfad zusätzlich kaputtgemacht
    // (User: "gestern kam beim Ride-Klick ein Safari-Permission-Prompt,
    // jetzt nicht mehr"). Der originale Flow wird wiederhergestellt, dazu
    // kommen sichtbare Debug-Alerts in CompassRose._toggle() und in
    // MapPage.startRide(), damit wir die tatsächliche Ursache (warum die
    // Rose nicht reagiert) sehen können.
    if (this._unsubHeading == null) {
      this._unsubHeading = deviceHeadingBus.subscribe((heading) => {
        this._lastHeading = heading;
        // Marker kann schon existieren (wenn wir z. B. aus 'paused' zurück-
        // kommen und der erste Heading-Tick vor dem ersten Position-Fix
        // ankommt). In dem Fall sofort rotieren — verhindert den "Dreieck
        // zeigt für 1 Frame in alte Richtung"-Effekt.
        if (this._marker) this._marker.setRotation(heading);
      });
      void deviceHeadingBus.requestPermissionIfNeeded();
    }

    console.info('[locate] start → waiting for fix');
  }

  private _onPosition(pos: GeolocationPosition): void {
    this._lastCoords = pos.coords;
    this._updateMarker(pos.coords);
    // An alle Subscriber (z. B. ElevationBadge) broadcasten. Auch im
    // paused-State sinnvoll — die Höhe soll weiter aktuell bleiben.
    positionBus.set(pos.coords);

    if (this._state === 'waiting') {
      // Stage 6m: One-shot-Modus springt direkt nach 'paused' statt 'active'.
      // Marker bleibt sichtbar und folgt der Position weiter (positionBus
      // wird auch in paused gefüttert), aber die Kamera folgt nicht mehr —
      // perfekt für den NAVI-Plan-Modus wo der User pannt.
      if (this._opts.oneShot) {
        this._state = 'paused';
        this._renderButton();
        this._flyToCoords(pos.coords);
        console.info('[locate] first fix → paused (one-shot)');
      } else {
        this._state = 'active';
        this._renderButton();
        this._flyToCoords(pos.coords);
        this._attachUserMoveListener();
        console.info('[locate] first fix → active (follow)');
      }
    } else if (this._state === 'active') {
      // Nachführen — nur Zentrum, Zoom bleibt. Der User darf reinzoomen.
      // ABER: nicht während der initiale flyTo noch läuft, sonst interrupten
      // wir die Zoom-Animation (siehe _flying-Feld für die Langform).
      if (this._flying) return;
      this._easeToCoords(pos.coords);
    }
    // paused: nur Marker-Update, Kamera bleibt stehen.
  }

  private _onError(err: GeolocationPositionError): void {
    console.warn('[locate] watchPosition error', { code: err.code, message: err.message });
    // Bei Timeout bleiben wir im waiting-State falls schon ein Fix kam —
    // sonst error.
    if (this._lastCoords) return;
    this._state = 'error';
    this._renderButton();
    this._stop(true);
  }

  /** Stoppt Tracking komplett. keepState=true lässt den Button im error-Zustand. */
  private _stop(keepState = false): void {
    if (this._watchId != null) {
      navigator.geolocation.clearWatch(this._watchId);
      this._watchId = null;
    }
    this._detachUserMoveListener();
    if (this._unsubHeading) {
      this._unsubHeading();
      this._unsubHeading = null;
    }
    if (this._marker) {
      this._marker.remove();
      this._marker = null;
    }
    if (!keepState) {
      this._state = 'off';
    }
    this._lastCoords = null;
    // Subscriber informieren dass keine Position mehr da ist.
    positionBus.set(null);
    this._renderButton();
    console.info('[locate] stopped');
  }

  private _flyToCoords(c: GeolocationCoordinates): void {
    const map = this._map;
    if (!map) return;
    // User-Wunsch: beim Zentrieren direkt auf die höchste verfügbare Zoomstufe.
    // map.getMaxZoom() gibt das in der Map-Config gesetzte Maximum (aktuell 15
    // wegen PMTiles-Generation bis z14). Wenn der User via lockZoom einen
    // festen Wert wünscht, überschreibt das hier.
    const targetZoom = this._opts.lockZoom ?? map.getMaxZoom();
    this._flying = true;
    map.flyTo({
      center: [c.longitude, c.latitude],
      zoom: targetZoom,
      duration: this._opts.flyDuration,
      // essential=true: läuft auch wenn "reduce-motion" an ist — damit der
      // User immer das visuelle Feedback "ich werde jetzt zentriert" kriegt.
      essential: true,
    });
    // moveend feuert wenn die Animation durch ist (auch bei Interrupt, z. B.
    // User-Drag → paused). Danach darf follow-easeTo wieder übernehmen.
    map.once('moveend', () => {
      this._flying = false;
    });
  }

  private _easeToCoords(c: GeolocationCoordinates): void {
    const map = this._map;
    if (!map) return;
    // Wave 14bp (Energiespar-Tipp #3): im Ride-Mode springt die Kamera statt
    // zu tweenen. Pro GPS-Fix (ca. 1 Hz bei maximumAge:1000) würde sonst ein
    // 500ms-easeTo laufen → ~30 rAF-Frames à 60fps, und direkt nach Abschluss
    // beginnt der nächste Fix den nächsten Tween. Effektiv läuft der Follow-
    // Tween also ~100% der Zeit; das GPU/CPU-Budget wird dauerhaft verheizt,
    // obwohl der sichtbare Unterschied zwischen jumpTo und 500ms-tween bei
    // 1Hz-Updates marginal ist (der Dot scheint "leicht anders" zu stottern,
    // aber das Trail-Overlay darunter bewegt sich mit). Praktisch kein
    // Framework-Visual-Lag, aber Akku-spürbar weniger Background-Work.
    //
    // Explore-Mode: easeTo bleibt. Dort kommen Fixes seltener (maximumAge:2s),
    // der Dot wandert in klar sichtbaren Sprüngen — ein weicher Tween wirkt
    // dort ruhiger und gut für "ich schaue zu, wo ich bin"-Szenarien.
    if (this._opts.trackingMode === 'ride') {
      map.jumpTo({
        center: [c.longitude, c.latitude],
      });
      return;
    }
    map.easeTo({
      center: [c.longitude, c.latitude],
      duration: this._opts.followDuration,
      essential: true,
    });
  }

  /**
   * Eigener blauer Pfeil als HTML-Marker.
   *
   * User-Wunsch: "statt eines runden kreises wo meine location ist, mach lieber
   * ein schönes blaues dreieck, bei der nordausrichztung zeigt es nach oben,
   * sonst in fahrtrichtung."
   *
   * Implementierung:
   *   - SVG-Dreieck (stilisiertes Kompass-Pfeil-Icon) mit weißem Border und
   *     subtilem Drop-Shadow für Lesbarkeit auf jedem Karten-Style.
   *   - rotationAlignment:'map' → das Marker-Element rotiert in Map-Koordinaten
   *     mit. setRotation() erwartet Grad im Uhrzeigersinn von Nord.
   *   - Wave 14am: rotation = Device-Heading (physische Blickrichtung), wenn
   *     verfügbar. Bevor Wave 14am war das GPS-coords.heading — das ist aber
   *     null im Stillstand und auch im 'heading up'-Map-Mode, wo die Map mit
   *     dem Kompass rotiert aber das Dreieck als Rückfall nicht.
   *     Damit folgt nun unabhängig vom Map-Mode:
   *       * Nord-oben Karte (bearing=0): visueller Winkel = device-heading →
   *         Dreieck zeigt in physische Blickrichtung. ✓
   *       * Heading-up-Modus (bearing=device-heading): visueller Winkel =
   *         device-heading − bearing = 0 → Dreieck zeigt nach oben. ✓
   *     GPS coords.heading ist Fallback, nur wenn vom Bus noch nichts kam
   *     (Desktop ohne Sensor, iOS ohne Permission, erste ms vor dem ersten
   *     DeviceOrientation-Event).
   */
  private _updateMarker(c: GeolocationCoordinates): void {
    const map = this._map;
    if (!map) return;
    // Device-Heading hat Vorrang — der Bus aktualisiert _lastHeading bereits
    // in Real-Time via Subscription (s. _start()). Hier füllen wir NUR dann
    // mit GPS-Heading, wenn noch kein Device-Event angekommen ist. Kriterium:
    // `deviceHeadingBus.latest()` ist noch null → benutze GPS als Bootstrap.
    // Sobald der Bus etwas liefert, nehmen wir GPS-Heading nicht mehr an, um
    // Hin-und-her-Jittern zwischen den zwei Quellen zu vermeiden.
    if (
      deviceHeadingBus.latest() == null &&
      c.heading != null &&
      Number.isFinite(c.heading)
    ) {
      this._lastHeading = c.heading;
    }
    if (this._marker) {
      this._marker.setLngLat([c.longitude, c.latitude]);
      this._marker.setRotation(this._lastHeading);
      return;
    }
    // SVG-Dreieck — 28px sichtbarer Pfeil. Wave 14k: voll-orange, passend zum
    // app-weiten Active-Accent (orange-500 = #f97316: aktive Tab-Pill, aktiver
    // Locate-Button, aktives Segment im Style-Switcher). Kein Blau-Fill mehr —
    // der frühere Kontrast-Trick "Blau + roter Stroke" war lesbar, passt aber
    // nicht mehr ins Farbschema.
    //
    // Damit der Pfeil auf hellen Karten UND auf dem Satellit sichtbar bleibt,
    // behalten wir einen dunklen Stroke — orange-900 (#7c2d12) hat genug
    // Kontrast gegen beides, bleibt aber in der Orange-Familie (kein
    // willkürliches Schwarz). Zusätzlich der drop-shadow wie bisher für
    // den Rand-Lift gegen weißes Trail-Halo (style.ts line-halo-color).
    //
    // Das innere "Notch" (14,20 statt 14,26) macht aus dem Dreieck einen
    // klassischen Kompass-Pfeil mit Tail.
    const el = document.createElement('div');
    el.setAttribute('aria-label', 'Your position');
    el.style.width = '28px';
    el.style.height = '28px';
    el.style.pointerEvents = 'none'; // Marker soll Taps an Map durchreichen
    el.style.filter = 'drop-shadow(0 1px 2px rgba(0,0,0,0.45))';
    el.innerHTML = `
      <svg viewBox="0 0 28 28" width="28" height="28" style="display:block">
        <polygon points="14,2 24,26 14,20 4,26"
                 fill="#f97316"
                 stroke="#7c2d12"
                 stroke-width="2.2"
                 stroke-linejoin="round" />
      </svg>
    `;
    this._marker = new maplibregl.Marker({
      element: el,
      pitchAlignment: 'map',
      rotationAlignment: 'map',
      rotation: this._lastHeading,
      // Wave 14bn: Terrain-Occlusion-Opacity auf 1.0 (default "0.2") stellen.
      // Wir haben setTerrain mit exaggeration=0.0001 aktiv (silent-terrain für
      // queryTerrainElevation), aber MapLibre rechnet die Occlusion auf den
      // rohen DEM-Höhen — Marker in Tälern oder hinter Hängen werden dadurch
      // mit 20% Opacity gerendert, obwohl das Terrain visuell flach ist. Das
      // erklärte den zeitweise "ausgegrauten" Position-Pfeil im Ride.
      // MapLibre 4.x nimmt einen String, nicht Zahl.
      opacityWhenCovered: '1',
    })
      .setLngLat([c.longitude, c.latitude])
      .addTo(map);
  }

  /**
   * Listener für explizite User-Gesten. Nur diese Events haben ein
   * originalEvent gesetzt wenn die Map programmatisch (essential:true)
   * bewegt wird — unsere eigenen Aufrufe leaken nicht.
   *
   * Bewusst KEIN 'touchstart' auf der Map: der würde bei jedem Tap feuern
   * (auch bei Taps die nicht zu Panning führen) und das Tracking voreilig
   * pausieren. 'dragstart' feuert erst bei tatsächlicher Bewegung.
   */
  private _attachUserMoveListener(): void {
    const map = this._map;
    if (!map) return;
    const handler = (e: unknown) => {
      // Typ lokal verengen: MapLibre liefert MapLibreEvent mit originalEvent
      // an alle Drag/Wheel/Pitch/Rotate-Listener, aber der on()-Overload hier
      // nimmt generisches unknown wg. String-Event-Name-Overloading.
      const evt = e as { originalEvent?: unknown };
      if (!evt.originalEvent) return;
      if (this._state !== 'active') return;
      this._state = 'paused';
      this._renderButton();
      console.info('[locate] user gesture → paused');
    };
    this._userMoveHandler = handler;
    map.on('dragstart', handler);
    map.on('wheel', handler);
    map.on('pitchstart', handler);
    map.on('rotatestart', handler);
  }

  private _detachUserMoveListener(): void {
    const map = this._map;
    if (!map || !this._userMoveHandler) return;
    map.off('dragstart', this._userMoveHandler);
    map.off('wheel', this._userMoveHandler);
    map.off('pitchstart', this._userMoveHandler);
    map.off('rotatestart', this._userMoveHandler);
    this._userMoveHandler = null;
  }

  /** Visueller State auf dem Button.
   *
   * Wir setzen via data-state die Zustandsklasse; die Farb-Akzente kommen
   * dann aus CSS (adt-ctrl-btn[data-state="active"] etc.). Das hält die
   * Style-Quelle an einer Stelle (styles/index.css) und erlaubt einfache
   * Theming-Änderungen ohne durch TS-Code zu grepen.
   */
  private _renderButton(): void {
    const btn = this._button;
    btn.dataset.state = this._state;
    // Inline-Farb-Reste aus früheren Varianten entfernen, falls das Control
    // ohne Rebuild State-Transitionen macht.
    btn.style.background = '';
    btn.style.color = '';

    // Einfache Unicode-Icons — kein Asset-Laden nötig, gut für Offline.
    switch (this._state) {
      case 'off':
        btn.textContent = '◎';
        btn.title = 'Show my location';
        break;
      case 'waiting':
        btn.textContent = '◐';
        btn.title = 'Getting location …';
        break;
      case 'active':
        btn.textContent = '◉';
        btn.title = 'Map follows you — tap again to stop';
        break;
      case 'paused':
        btn.textContent = '◎';
        btn.title = 'Paused (you moved the map) — tap to re-center';
        break;
      case 'error':
        btn.textContent = '⊗';
        btn.title = 'Location unavailable';
        break;
    }
  }
}
